import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from './cn'

export interface TabItem {
  id: string
  label: ReactNode
  /**
   * Panel content for this tab. Optional: omit when supplying the active panel
   * via the `tabs` + `children` form (see {@link TabsProps}).
   */
  content?: ReactNode
  disabled?: boolean
}

/** Lightweight tab descriptor for the `tabs` (value/label) form. */
export interface TabsValueItem {
  value: string
  label: ReactNode
  disabled?: boolean
}

export interface TabsProps {
  /** Canonical form — each item carries its own embedded `content`. */
  items?: TabItem[]
  /**
   * Alternate (fork) form — a `{ value, label }[]` tablist with the active
   * panel supplied via `children`. Normalized into `items` (value → id).
   */
  tabs?: TabsValueItem[]
  /** Accessible label for the tablist. */
  ariaLabel?: string
  /** Panel content for the `tabs`/value form (rendered for the active tab). */
  children?: ReactNode
  defaultTabId?: string
  value?: string
  onChange?: (tabId: string) => void
  className?: string
}

/**
 * Tabs — accessible tablist with keyboard arrow nav (WCAG 2.1).
 * Controlled (pass `value` + `onChange`) or uncontrolled (`defaultTabId`).
 *
 * Two interchangeable content contracts (backward-compatible superset):
 *  - canonical: `<Tabs items={[{ id, label, content }]} />`
 *  - fork:      `<Tabs tabs={[{ value, label }]} ariaLabel="…">{panel}</Tabs>`
 */
export function Tabs({ items, tabs, ariaLabel, children, defaultTabId, value, onChange, className = '' }: TabsProps) {
  const normalizedItems: TabItem[] =
    items ?? tabs?.map((tab) => ({ id: tab.value, label: tab.label, disabled: tab.disabled })) ?? []
  const gid = useId()
  const [internal, setInternal] = useState<string>(defaultTabId || normalizedItems[0]?.id || '')
  const activeId = value ?? internal
  const setActive = (id: string) => {
    if (value === undefined) setInternal(id)
    onChange?.(id)
  }

  const activeIndex = Math.max(
    0,
    normalizedItems.findIndex((i) => i.id === activeId)
  )
  const activeItem = normalizedItems[activeIndex]

  const onKeyDown = (e: React.KeyboardEvent) => {
    const enabled = normalizedItems.filter((i) => !i.disabled)
    if (enabled.length === 0) return
    const enabledIds = enabled.map((i) => i.id)
    const idx = enabledIds.indexOf(activeId)
    let nextIdx: number | null = null
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % enabledIds.length
    else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + enabledIds.length) % enabledIds.length
    else if (e.key === 'Home') nextIdx = 0
    else if (e.key === 'End') nextIdx = enabledIds.length - 1
    if (nextIdx !== null) {
      e.preventDefault()
      setActive(enabledIds[nextIdx])
      const nextEl = document.getElementById(`${gid}-tab-${enabledIds[nextIdx]}`)
      nextEl?.focus()
    }
  }

  return (
    <div className={cn('w-full', className)}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation="horizontal"
        onKeyDown={onKeyDown}
        className="flex min-w-0 max-w-full flex-nowrap overflow-x-auto overscroll-x-contain border-b border-line shadow-scroll-hint"
      >
        {normalizedItems.map((item) => {
          const active = item.id === activeId
          return (
            <button
              key={item.id}
              id={`${gid}-tab-${item.id}`}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`${gid}-panel-${item.id}`}
              tabIndex={active ? 0 : -1}
              disabled={item.disabled}
              onClick={() => setActive(item.id)}
              className={cn(
                'shrink-0 whitespace-nowrap px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors duration-fast ease-out-soft',
                'focus-visible:outline-none focus-visible:shadow-focus',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                active
                  ? 'border-vjal-500 text-vjal-700'
                  : 'border-transparent text-ink-muted hover:text-ink-body hover:border-line'
              )}
            >
              {item.label}
            </button>
          )
        })}
      </div>
      <div
        role="tabpanel"
        id={`${gid}-panel-${activeItem?.id}`}
        tabIndex={0}
        className="pt-4 focus-visible:outline-none focus-visible:shadow-focus rounded"
      >
        {children ?? activeItem?.content}
      </div>
    </div>
  )
}
